import { useState } from 'react';
import { Button } from '@mailflow/ui';
import {
  useEmpfaengerVorschau,
  type AudienceBreakdown,
  type AudienceRef,
} from '../lib/campaigns.js';

/**
 * Empfänger wählen — und sehen, wie die Zahl zustande kommt.
 *
 * ZWEI DINGE, DIE VORHER FEHLTEN:
 *
 *   1. MEHRERE LISTEN UND SEGMENTE. Das Datenmodell konnte es von Anfang an
 *      (`audience.include[]` / `audience.exclude[]`), die Oberfläche erlaubte
 *      aber genau eines: `includeTarget` war eine einzelne Zeichenkette. Wer
 *      zwei Listen anschreiben wollte, musste zwei Kampagnen anlegen.
 *
 *   2. DIE HERLEITUNG. „4.453 Empfänger“ ohne Erklärung ist Vertrauenssache.
 *      Wer wissen will, warum aus 4.812 nur 4.453 wurden, soll es aufklappen
 *      können, statt in der Datenbank nachzusehen oder zu fragen.
 */

interface Auswahl {
  id: string;
  name: string;
  anzahl: number | null;
}

export type { AudienceRef };

export interface AudienceBlockProps {
  listen: Auswahl[];
  segmente: Auswahl[];
  include: AudienceRef[];
  exclude: AudienceRef[];
  enforceManual?: boolean;
  enforceUnsubscribe?: boolean;
  enforceNoInterest?: boolean;
  excludeHasWonDeal?: boolean;
  onChange: (
    include: AudienceRef[],
    exclude: AudienceRef[],
    toggles?: {
      enforceManual: boolean;
      enforceUnsubscribe: boolean;
      enforceNoInterest: boolean;
      excludeHasWonDeal: boolean;
    },
  ) => void;
}

const POSTEN: Array<{ feld: keyof AudienceBreakdown; text: string }> = [
  { feld: 'mehrfach', text: 'in mehreren Listen (nur einmal gezählt)' },
  { feld: 'ausgeschlossen', text: 'durch Ausschlusslisten' },
  { feld: 'geloescht', text: 'Kontakt gelöscht' },
  { feld: 'abgemeldet', text: 'abgemeldet' },
  { feld: 'unzustellbar', text: 'dauerhaft unzustellbar' },
  { feld: 'beschwert', text: 'hat sich beschwert' },
  { feld: 'gesperrt', text: 'auf der Sperrliste' },
];

export function AudienceBlock({
  listen,
  segmente,
  include,
  exclude,
  enforceManual = true,
  enforceUnsubscribe = true,
  enforceNoInterest = true,
  excludeHasWonDeal = true,
  onChange,
}: AudienceBlockProps) {
  const [offen, setOffen] = useState(false);
  const [manualToggle, setManualToggle] = useState(enforceManual);
  const [unsubToggle, setUnsubToggle] = useState(enforceUnsubscribe);
  const [noInterestToggle, setNoInterestToggle] = useState(enforceNoInterest);
  const [wonDealToggle, setWonDealToggle] = useState(excludeHasWonDeal);

  const { vorschau, laeuft, fehler } = useEmpfaengerVorschau({
    include,
    exclude,
    enforceManual: manualToggle,
    enforceUnsubscribe: unsubToggle,
    enforceNoInterest: noInterestToggle,
    excludeHasWonDeal: wonDealToggle,
  });

  function notifyChange(
    newInc = include,
    newExc = exclude,
    mToggle = manualToggle,
    uToggle = unsubToggle,
    niToggle = noInterestToggle,
    wToggle = wonDealToggle,
  ) {
    onChange(newInc, newExc, {
      enforceManual: mToggle,
      enforceUnsubscribe: uToggle,
      enforceNoInterest: niToggle,
      excludeHasWonDeal: wToggle,
    });
  }

  function nameVon(ref: AudienceRef): string {
    const quelle = ref.kind === 'list' ? listen : segmente;
    return quelle.find((e) => e.id === ref.id)?.name ?? 'Unbekannt';
  }

  function hinzufuegen(ziel: 'include' | 'exclude', wert: string) {
    if (!wert) return;
    const [kind, id] = wert.split(':') as ['list' | 'segment', string];
    if (!kind || !id) return;

    const liste = ziel === 'include' ? include : exclude;
    if (liste.some((r) => r.kind === kind && r.id === id)) return;

    const neu = [...liste, { kind, id }];
    notifyChange(ziel === 'include' ? neu : include, ziel === 'exclude' ? neu : exclude);
  }

  function entfernen(ziel: 'include' | 'exclude', ref: AudienceRef) {
    const gefiltert = (ziel === 'include' ? include : exclude).filter(
      (r) => !(r.kind === ref.kind && r.id === ref.id),
    );
    notifyChange(ziel === 'include' ? gefiltert : include, ziel === 'exclude' ? gefiltert : exclude);
  }

  return (
    <div className="stack">
      <Auswahlzeile
        titel="Empfänger"
        hinweis="Listen und Segmente lassen sich beliebig kombinieren. Doppelte werden nur einmal angeschrieben."
        listen={listen}
        segmente={segmente}
        gewaehlt={include}
        nameVon={nameVon}
        onHinzufuegen={(wert) => hinzufuegen('include', wert)}
        onEntfernen={(ref) => entfernen('include', ref)}
        leerText="Noch keine Liste gewählt — ohne Empfänger lässt sich nicht senden."
      />

      <Auswahlzeile
        titel="Ausschliessen"
        hinweis="Wer hier steht, bekommt nichts — unabhängig davon, in welcher Empfängerliste er sonst steht."
        listen={listen}
        segmente={segmente}
        gewaehlt={exclude}
        nameVon={nameVon}
        onHinzufuegen={(wert) => hinzufuegen('exclude', wert)}
        onEntfernen={(ref) => entfernen('exclude', ref)}
        leerText="Kein Ausschluss."
      />

      {/* Sperrlisten-Steuerung */}
      <div className="field">
        <span className="field__label">Sperrlisten-Filter (Compliance & Schutz)</span>
        <div className="stack" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-1)' }}>
          <label className="check">
            <input
              type="checkbox"
              checked={manualToggle}
              onChange={(e) => {
                const checked = e.target.checked;
                setManualToggle(checked);
                notifyChange(include, exclude, checked, unsubToggle, noInterestToggle, wonDealToggle);
              }}
            />
            <span>
              <strong>1. Manuelle Sperrliste aktivieren</strong> (Manuell gesetzte Adressen & Domains ausschließen)
            </span>
          </label>

          <label className="check">
            <input
              type="checkbox"
              checked={unsubToggle}
              onChange={(e) => {
                const checked = e.target.checked;
                setUnsubToggle(checked);
                notifyChange(include, exclude, manualToggle, checked, noInterestToggle, wonDealToggle);
              }}
            />
            <span>
              <strong>2. Unsubscribe / Abgemeldet-Sperrliste aktivieren</strong> (Abgemeldete Personen ausschließen)
            </span>
          </label>

          <label className="check">
            <input
              type="checkbox"
              checked={noInterestToggle}
              onChange={(e) => {
                const checked = e.target.checked;
                setNoInterestToggle(checked);
                notifyChange(include, exclude, manualToggle, unsubToggle, checked, wonDealToggle);
              }}
            />
            <span>
              <strong>3. Kein Interesse-Sperrliste aktivieren</strong> (Opt-Out / Keine Interesse Kontakte ausschließen)
            </span>
          </label>

          <label className="check">
            <input
              type="checkbox"
              checked={wonDealToggle}
              onChange={(e) => {
                const checked = e.target.checked;
                setWonDealToggle(checked);
                notifyChange(include, exclude, manualToggle, unsubToggle, noInterestToggle, checked);
              }}
            />
            <span>
              <strong>4. Kontakte mit bestehendem Rezensionsfall / Auftrag ausschließen (Standard: Aktiviert)</strong> (Keine Mails an gewonnene Kunden mit Rezensionsfall)
            </span>
          </label>
        </div>
        <span className="field__hint">
          Echtzeit-Aktualisierung der Netto-Empfängerzahl berücksichtigt die gewählten Sperrlisten, gesperrte Domains und Kunden mit Rezensionsfall.
        </span>
      </div>

      {fehler && (
        <div className="alert" data-tone="danger" role="alert">
          {fehler} — die Empfängerzahl ist damit <strong>unbekannt</strong>, nicht null.
        </div>
      )}

      {include.length > 0 && !fehler && (
        <div className="check" data-status={vorschau && vorschau.netCount === 0 ? 'warn' : 'ok'}>
          <div className="check__head">
            {laeuft ? (
              <span className="muted">Empfänger werden ermittelt …</span>
            ) : vorschau ? (
              <strong className="text-lg">
                {vorschau.netCount.toLocaleString('de-DE')} Empfänger
              </strong>
            ) : (
              <span className="muted">—</span>
            )}
            {vorschau && (
              <Button variant="ghost" onClick={() => setOffen(!offen)}>
                {offen ? 'Herleitung ausblenden' : 'Wie kommt die Zahl zustande?'}
              </Button>
            )}
          </div>

          {vorschau && offen && <Herleitung posten={vorschau.aufschluesselung} />}

          {vorschau && vorschau.netCount === 0 && (
            <p className="muted text-sm">
              Es bleibt niemand übrig. Prüfe die Ausschlusslisten und die Sperrliste — die
              Herleitung sagt, wo die Kontakte geblieben sind.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Auswahlzeile({
  titel,
  hinweis,
  listen,
  segmente,
  gewaehlt,
  nameVon,
  onHinzufuegen,
  onEntfernen,
  leerText,
}: {
  titel: string;
  hinweis: string;
  listen: Auswahl[];
  segmente: Auswahl[];
  gewaehlt: AudienceRef[];
  nameVon: (ref: AudienceRef) => string;
  onHinzufuegen: (wert: string) => void;
  onEntfernen: (ref: AudienceRef) => void;
  leerText: string;
}) {
  const [wahl, setWahl] = useState('');

  return (
    <div className="field">
      <span className="field__label">{titel}</span>

      {gewaehlt.length === 0 ? (
        <p className="muted text-sm">{leerText}</p>
      ) : (
        <div className="tag-row">
          {gewaehlt.map((ref) => (
            <span key={`${ref.kind}:${ref.id}`} className="ausnahmetag">
              {ref.kind === 'segment' ? 'Segment: ' : ''}
              {nameVon(ref)}
              <button
                type="button"
                className="ausnahmetag__weg"
                aria-label={`${nameVon(ref)} entfernen`}
                onClick={() => onEntfernen(ref)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <select
        className="input"
        value={wahl}
        aria-label={`${titel} hinzufügen`}
        onChange={(e) => {
          onHinzufuegen(e.target.value);
          setWahl('');
        }}
      >
        <option value="">Hinzufügen …</option>
        {listen.length > 0 && (
          <optgroup label="Listen">
            {listen.map((l) => (
              <option key={l.id} value={`list:${l.id}`}>
                {l.name}
                {l.anzahl !== null ? ` (${l.anzahl.toLocaleString('de-DE')})` : ''}
              </option>
            ))}
          </optgroup>
        )}
        {segmente.length > 0 && (
          <optgroup label="Segmente">
            {segmente.map((s) => (
              <option key={s.id} value={`segment:${s.id}`}>
                {s.name}
                {s.anzahl !== null ? ` (${s.anzahl.toLocaleString('de-DE')})` : ''}
              </option>
            ))}
          </optgroup>
        )}
      </select>

      <span className="field__hint">{hinweis}</span>
    </div>
  );
}

/**
 * Die Rechnung, Zeile für Zeile.
 *
 * Posten mit 0 werden weggelassen — eine Liste aus sieben Nullen erklärt
 * nichts, sie verdeckt die zwei Zeilen, auf die es ankommt.
 */
function Herleitung({ posten }: { posten: AudienceBreakdown }) {
  const zeilen = POSTEN.filter((p) => (posten[p.feld] as number) > 0);

  return (
    <table className="herleitung">
      <tbody>
        <tr>
          <td className="herleitung__zahl">{posten.brutto.toLocaleString('de-DE')}</td>
          <td>in den gewählten Listen und Segmenten</td>
        </tr>
        {zeilen.map((p) => (
          <tr key={p.feld}>
            <td className="herleitung__zahl">
              −{(posten[p.feld] as number).toLocaleString('de-DE')}
            </td>
            <td>{p.text}</td>
          </tr>
        ))}
        <tr className="herleitung__summe">
          <td className="herleitung__zahl">{posten.netto.toLocaleString('de-DE')}</td>
          <td>Empfänger</td>
        </tr>
      </tbody>
    </table>
  );
}
